<template>
  <div class="slider" :class="shape">
    <span :lastKey="convertedMin" class="slider__parameter">
      {{ parseFloat((convertedMin + displayOffsetConverted).toFixed(3)) }}
    </span>

    <input
      class="slider__input"
      :class="{ 'range-at-end': isZeroRange }"
      type="range"
      :value="displayedLocalValue"
      :step="convertedStep"
      :min="convertedMin"
      :max="convertedMax"
      @input="handleInput($event.target.value)"
      @change="commitValue"
      :style="inputBackground"
    />

    <span :lastKey="convertedMax" class="slider__parameter">
      {{ parseFloat((convertedMax + displayOffsetConverted).toFixed(3)) }}
    </span>
  </div>
</template>

<script>
export default {
  name: 'Slider',
  props: {
    shape: { type: String, default: 'pill' },
    borderRadius: { type: String, default: '0px' },
    activeColor: { type: String, default: 'black' },
    inactiveColor: { type: String, default: 'transparent' },
    valorSel: { type: [Number, String], default: 0 },
    step: { type: Number, default: 1 },
    min: { type: Number, default: 0 },
    max: { type: Number, default: 10 },
    units: { type: String, default: 'm' },
    displayOffset: { type: Number, default: 0 },
  },
  data() {
    return {
      localValue: this.valorSel,
      isAdjusting: false,
    };
  },
  watch: {
    valorSel(newVal) {
      if (newVal !== this.localValue) {
        this.localValue = newVal;
      }
    },
    min(newMin) {
      if (this.localValue < newMin && !this.isAdjusting) {
        this.isAdjusting = true;
        this.localValue = newMin;
        this.$emit('valor', newMin);
        this.isAdjusting = false;
      }
    },
    max(newMax) {
      if (this.localValue > newMax && !this.isAdjusting) {
        this.isAdjusting = true;
        this.localValue = newMax;
        this.$emit('valor', newMax);
        this.isAdjusting = false;
      }
    },
  },
  computed: {
    displayOffsetConverted() {
      switch (this.units) {
        case 'mm': return this.displayOffset * 1000;
        case 'cm': return this.displayOffset * 100;
        default: return this.displayOffset;
      }
    },
    convertedStep() {
      switch (this.units) {
        case 'mm':
          return this.step * 1000;
        case 'cm':
          return this.step * 100;
        default:
          return this.step;
      }
    },
    convertedMin() {
      switch (this.units) {
        case 'mm':
          return this.min * 1000;
        case 'cm':
          return this.min * 100;
        default:
          return this.min;
      }
    },
    convertedMax() {
      switch (this.units) {
        case 'mm':
          return this.max * 1000;
        case 'cm':
          return this.max * 100;
        default:
          return this.max;
      }
    },
    // When min and max are equal, force the value to the max so the thumb sits at 100%
    displayedLocalValue() {
      if (this.isZeroRange) {
        return this.convertedMax;
      }
      switch (this.units) {
        case 'mm':
          return this.localValue * 1000;
        case 'cm':
          return this.localValue * 100;
        default:
          return this.localValue;
      }
    },
    inputBackground() {
      const minVal = this.convertedMin;
      const maxVal = this.convertedMax;
      const range = maxVal - minVal;
      let percentage = 0;

      if (range === 0) {
        percentage = 100;
      } else {
        const clampedVal = Math.min(
          Math.max(this.displayedLocalValue, minVal),
          maxVal
        );
        percentage = ((clampedVal - minVal) / range) * 100;
      }

      return {
        background: `linear-gradient(to right, ${this.activeColor} 0%, ${this.activeColor} ${percentage}%, ${this.inactiveColor} ${percentage}%, ${this.inactiveColor} 100%)`,
      };
    },
    // Computed flag to indicate when the slider has zero range
    isZeroRange() {
      return this.convertedMin === this.convertedMax;
    },
  },
  methods: {
    handleInput(displayVal) {
      let baseVal = Number(displayVal);
      switch (this.units) {
        case 'mm':
          baseVal /= 1000;
          break;
        case 'cm':
          baseVal /= 100;
          break;
      }
      this.localValue = baseVal;
      this.$emit('updateLocal', baseVal);
    },
    commitValue() {
      this.$emit('valor', this.localValue);
    },
  },
};
</script>

<style scoped lang="scss">
.slider {
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;

  &__parameter {
    display: flex;
    justify-content: center;
    align-items: center;
    width: 30px;
    height: 30px;
    font-size: 13px;
    &:first-child {
      margin-right: 10px;
    }
    &:last-child {
      margin-left: 10px;
    }
  }

  &__input {
    cursor: pointer;
    outline: none;
    appearance: none;
    width: 100%;
    height: 4px;
    border-radius: v-bind(borderRadius);
    &::-webkit-slider-thumb {
      -webkit-appearance: none;
      appearance: none;
      width: 15px;
      height: 15px;
      border-radius: 100%;
      background: #000000;
      box-shadow: none;
    }
    &::-moz-range-thumb {
      border: none;
      width: 15px;
      height: 15px;
      border-radius: 5px;
      background: #000000;
      box-shadow: none;
    }
  }

  // This rule applies when the slider has zero range.
  &__input.range-at-end {
    /* Flip the slider track horizontally */
    transform: scaleX(-1);
  }
  &__input.range-at-end::-webkit-slider-thumb {
    /* Re-flip the thumb so it appears normally */
    transform: scaleX(-1);
  }
  &__input.range-at-end::-moz-range-thumb {
    transform: scaleX(-1);
  }

  &.pill {
    & .slider__input {
      height: 4px;
      &::-webkit-slider-thumb {
        width: 4px;
        height: 20px;
        border-radius: 5px;
      }
      &::-moz-range-thumb {
        width: 4px;
        height: 20px;
        border-radius: 5px;
      }
    }
  }
}
</style>
